import type { DefaultContext, ErrorLike } from "@apollo/client";
import type { ApolloForbiddenToastContext } from "@/config/apollo";
import { isForbiddenApiError } from "@/utils/forbiddenApiError";

export type ForbiddenAwareOnError = (
  error: ErrorLike,
  ...args: unknown[]
) => void;

export type ApolloNetworkOptionsSlice = Record<string, unknown> & {
  onError?: ForbiddenAwareOnError;
  context?: unknown;
};

function readSkipGlobalForbiddenToastFlag(context: unknown): boolean {
  if (context === null || typeof context !== "object") {
    return false;
  }
  return (context as ApolloForbiddenToastContext).skipGlobalError === true;
}

function wrapForbiddenAwareOnError(
  plainContextCarryingSkipGlobalFlag: DefaultContext | undefined,
  callerSuppliedOnError: ForbiddenAwareOnError | undefined
): ForbiddenAwareOnError | undefined {
  if (!callerSuppliedOnError) {
    return undefined;
  }
  return (error: ErrorLike, ...apolloCallbackRestArgs: unknown[]) => {
    const mutateFunctionSecondArgument = apolloCallbackRestArgs[0] as
      { context?: unknown } | undefined;
    const callerOptedOutOfGlobalForbiddenToast =
      readSkipGlobalForbiddenToastFlag(plainContextCarryingSkipGlobalFlag) ||
      readSkipGlobalForbiddenToastFlag(mutateFunctionSecondArgument?.context);
    if (!callerOptedOutOfGlobalForbiddenToast && isForbiddenApiError(error)) {
      return;
    }
    callerSuppliedOnError(error, ...apolloCallbackRestArgs);
  };
}

export function withWrappedForbiddenOnError<
  T extends ApolloNetworkOptionsSlice,
>(
  incomingApolloOptions: T,
  plainContextCarryingSkipGlobalFlag: DefaultContext | undefined
): T {
  const {
    onError: callerSuppliedOnError,
    context,
    ...allOtherOptionKeys
  } = incomingApolloOptions;
  return {
    ...allOtherOptionKeys,
    context,
    onError: wrapForbiddenAwareOnError(
      plainContextCarryingSkipGlobalFlag,
      callerSuppliedOnError
    ),
  } as T;
}
